/** The evidence a diagnose run assembled — the Evidence panel's data source.
 *
 *  A server endpoint rather than a client fetch of the `ai_context.json`
 *  artifact: it lives on the debug S3 bucket, which the console CSP's
 *  `connect-src` excludes, and the permission that guards it belongs on the
 *  server. Gated by the AI permission of the task's own type. */

import { apiGet } from './client';
import type { DiagnoseThreadAttachment } from './diagnoseAttachments';

/** One console record a section was rendered from. `model` is a key of the
 *  MODEL_ROUTES table in `src/utils/gridLinks.ts`; a contract test asserts the
 *  backend cannot name one this console has no route for. */
export interface DiagnoseContextRef {
  model: string;
  id: string;
  label: string;
}

/** One evidence section, or one tool the run offered. */
export interface DiagnoseContextEntry {
  name: string;
  title: string;
  summary: string;
  /** 'prompt' = the model was shown this without asking. */
  origin: 'prompt' | 'tool';
  /** Catalog group key; `manifest.groups` gives order and titles. */
  group: string;
  status:
    | 'ok'
    | 'empty'
    | 'unavailable'
    | 'expired'
    | 'error'
    | 'pending'
    | 'unsupported';
  /** Why a non-ok section is not ok. */
  note: string;
  /** Captured size; null when the section was never materialized. */
  chars: number | null;
  pages: number;
  /** Chars the section cap dropped — non-zero means evidence was cut. */
  truncated_chars: number;
  lazy: boolean;
  /** Did the model actually open this? */
  read: boolean;
  plan_kind: string;
  /** What a frozen plan fetches, in prose — never the query itself. */
  plan_summary: string;
  refs: DiagnoseContextRef[];
  has_text: boolean;
  /** Entries of the `attachments` group ("Attached files") — one per file
   *  attached anywhere in the thread. The bytes come from
   *  `/diagnose/{task_id}/attachments/{id}/`, never from `?section=`. */
  attachment?: DiagnoseThreadAttachment | null;
}

export interface DiagnoseContextManifest {
  available: boolean;
  /** Only when available is false. */
  reason?: 'expired' | 'not_captured' | 'pending';
  task_id: string;
  task_type: string;
  architecture: 'bundle' | 'eager';
  preview: boolean;
  subject: { kind: string; id: string } | null;
  release_id: string;
  context_source: '' | 'captured' | 'rebuilt';
  totals: {
    prompt_chars: number;
    total_chars: number | null;
    delivered_chars: number | null;
    tool_calls: number | null;
    sections: number;
    sections_read: number;
    truncated_chars: number;
  };
  groups: { key: string; title: string }[];
  entries: DiagnoseContextEntry[];
  tools: DiagnoseContextEntry[];
  /** The run's own per-source map. Its keys are NOT the section names for the
   *  eager tools — never join the two by name. */
  availability: Record<string, string>;
}

export interface DiagnoseContextSection {
  task_id: string;
  section: string;
  page: number;
  pages: number;
  text: string;
  /** false = a lazy section the run never opened; there is nothing to show. */
  captured: boolean;
}

export const diagnoseContextApi = {
  /** The manifest for a run. Any thread member resolves to its anchor. */
  manifest: (taskId: string) =>
    apiGet<DiagnoseContextManifest>(`/diagnose/${taskId}/context/`),

  section: (taskId: string, section: string, page = 1) =>
    apiGet<DiagnoseContextSection>(`/diagnose/${taskId}/context/`, {
      params: { section, page },
    }),
};
